<template>
  <div class="settings">
    <div v-if="success" class="success-message-top">{{ $t('raSettings.states.success') }}</div>
    <div v-if="loading" class="loading">{{ $t('raSettings.states.loading') }}</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <template v-else>
      <!-- Tabs Navigation -->
      <div class="tabs-navigation">
        <button 
          v-for="tab in tabs" 
          :key="tab.id"
          class="tab-button"
          :class="{ active: activeTab === tab.id }"
          @click="activeTab = tab.id"
        >
          <i :class="tab.icon"></i>
          {{ $t(`raSettings.tabs.${tab.id}`) }}
        </button>
      </div>

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- Profile Tab -->
        <div v-show="activeTab === 'profile'" class="tab-pane">
          <div class="settings-section">
            <h2>{{ $t('raSettings.profile.title') }}</h2>
            <div class="profile-form">
              <div class="avatar-section">
                <div class="avatar">
                  <img :src="profile.avatar || '@/assets/avatar.png'" alt="Profile Avatar" />
                  <button class="change-avatar-btn">
                    <i class="fas fa-camera"></i>
                    {{ $t('raSettings.profile.avatar.changePhoto') }}
                  </button>
                </div>
              </div>
              <div class="form-group">
                <label>{{ $t('raSettings.profile.form.firstName.label') }}</label>
                <input type="text" v-model="profile.firstName" :placeholder="$t('raSettings.profile.form.firstName.placeholder')">
              </div>
              <div class="form-group">
                <label>{{ $t('raSettings.profile.form.lastName.label') }}</label>
                <input type="text" v-model="profile.lastName" :placeholder="$t('raSettings.profile.form.lastName.placeholder')">
              </div>
              <div class="form-group">
                <label>{{ $t('raSettings.profile.form.email.label') }}</label>
                <input type="email" v-model="profile.email" :placeholder="$t('raSettings.profile.form.email.placeholder')" disabled>
              </div>
              <div class="form-group">
                <label>{{ $t('raSettings.profile.form.phone.label') }}</label>
                <input type="tel" v-model="profile.phone" :placeholder="$t('raSettings.profile.form.phone.placeholder')">
              </div>
              <button class="save-btn" @click="saveProfile">{{ $t('raSettings.profile.form.saveChanges') }}</button>
            </div>
          </div>
        </div>

        <!-- Security Tab -->
        <div v-show="activeTab === 'security'" class="tab-pane">
          <div class="settings-section">
            <h2>{{ $t('raSettings.security.title') }}</h2>
            <div class="security-form">
              <div class="form-group">
                <label>{{ $t('raSettings.security.form.currentPassword.label') }}</label>
                <div class="password-input-wrapper">
                  <input 
                    :type="showCurrentPassword ? 'text' : 'password'" 
                    v-model="security.currentPassword" 
                    :placeholder="$t('raSettings.security.form.currentPassword.placeholder')"
                  >
                  <i 
                    :class="['password-toggle', showCurrentPassword ? 'fas fa-eye-slash' : 'fas fa-eye']"
                    @click="showCurrentPassword = !showCurrentPassword"
                  ></i>
                </div>
              </div>
              <div class="form-group">
                <label>{{ $t('raSettings.security.form.newPassword.label') }}</label>
                <div class="password-input-wrapper">
                  <input 
                    :type="showNewPassword ? 'text' : 'password'" 
                    v-model="security.newPassword" 
                    :placeholder="$t('raSettings.security.form.newPassword.placeholder')"
                  >
                  <i 
                    :class="['password-toggle', showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye']"
                    @click="showNewPassword = !showNewPassword"
                  ></i>
                </div>
              </div>
              <div class="form-group">
                <label>{{ $t('raSettings.security.form.confirmPassword.label') }}</label>
                <div class="password-input-wrapper">
                  <input 
                    :type="showConfirmPassword ? 'text' : 'password'" 
                    v-model="security.confirmPassword" 
                    :placeholder="$t('raSettings.security.form.confirmPassword.placeholder')"
                  >
                  <i 
                    :class="['password-toggle', showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye']"
                    @click="showConfirmPassword = !showConfirmPassword"
                  ></i>
                </div>
              </div>
              <div class="form-actions">
                <p v-if="securityError" class="error-message">{{ securityError }}</p>
                <p v-if="securitySuccess" class="success-message">{{ securitySuccess }}</p>
                <button 
                  class="save-btn" 
                  @click="updatePassword"
                  :disabled="isUpdatingPassword"
                >
                  {{ isUpdatingPassword ? $t('raSettings.security.form.updating') : $t('raSettings.security.form.updatePassword') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import '@/styles/Settings.css'
import { useI18n } from 'vue-i18n'
import agentService from '@/services/agentService'
import authService from '@/services/authService'
import axiosInstance from '@/services/axiosInstance'

export default {
  name: 'AgentSettings',
  setup() {
    const { t } = useI18n()
    return { t }
  },
  data() {
    return {
      activeTab: 'profile',
      tabs: [
        { id: 'profile', name: 'Profile', icon: 'fas fa-user' },
        { id: 'security', name: 'Security', icon: 'fas fa-shield-alt' }
      ],
      profile: {
        id: null,
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        avatar: null
      },
      security: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
      securityError: null,
      securitySuccess: null,
      isUpdatingPassword: false,
      loading: false,
      error: null,
      success: null,
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false
    }
  },
  created() {
    authService.initAuth()
    
    if (!authService.isAgentAuthenticated()) {
      this.error = this.t('raSettings.states.error')
      return
    }

    this.loadAgentProfile()
  },
  methods: {
    async loadAgentProfile() {
      if (!authService.isAgentAuthenticated()) {
        this.error = this.t('raSettings.states.error')
        return
      }

      this.loading = true
      this.error = null
      try {
        const agentEmail = localStorage.getItem('agentEmail')
        const response = await authService.getAgentByEmail(agentEmail)
        if (response.success && response.agent) {
          this.profile = {
            id: response.agent.id,
            firstName: response.agent.firstName,
            lastName: response.agent.lastName,
            email: response.agent.email,
            phone: response.agent.phone,
            avatar: response.agent.avatar
          }
        } else {
          throw new Error(response.message || this.t('raSettings.states.error'))
        }
      } catch (error) {
        console.error('Error loading agent data:', error)
        this.error = error.message || this.t('raSettings.states.error')
      } finally {
        this.loading = false
      }
    },
    async saveProfile() {
      if (!authService.isAgentAuthenticated()) {
        this.error = this.t('raSettings.states.error')
        return
      }

      this.loading = true
      this.error = null
      this.success = null
      try {
        const profileData = {
          firstName: this.profile.firstName,
          lastName: this.profile.lastName,
          phone: this.profile.phone
        }
        const response = await agentService.updateAgent(this.profile.id, profileData)
        if (response.success) {
          this.success = this.t('raSettings.states.success')
          if (response.agent) {
            this.$store.dispatch('updateAgentProfile', response.agent)
          } else {
            this.$store.dispatch('updateAgentProfile', { 
              id: this.profile.id, 
              ...profileData 
            })
          }
          setTimeout(() => { this.success = null }, 3000)
        } else {
          throw new Error(response.message || this.t('raSettings.states.error'))
        }
      } catch (error) {
        console.error('Error saving profile:', error)
        this.error = error.message || this.t('raSettings.states.error')
      } finally {
        this.loading = false
      }
    },
    async updatePassword() {
      this.securityError = null
      this.securitySuccess = null

      if (!this.security.currentPassword || !this.security.newPassword || !this.security.confirmPassword) {
        this.securityError = this.t('raSettings.security.errors.required')
        return
      }

      if (this.security.newPassword !== this.security.confirmPassword) {
        this.securityError = this.t('raSettings.security.errors.mismatch')
        return
      }

      if (this.security.newPassword.length < 8) {
        this.securityError = this.t('raSettings.security.errors.tooShort')
        return
      }

      try {
        this.isUpdatingPassword = true
        const passwordData = {
          currentPassword: this.security.currentPassword,
          newPassword: this.security.newPassword
        }
        const token = localStorage.getItem('agentToken')
        const response = await axiosInstance.put(
          '/agents/update-password',
          passwordData,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        if (response.data.success) {
          this.securitySuccess = this.t('raSettings.security.success')
          this.security.currentPassword = ''
          this.security.newPassword = ''
          this.security.confirmPassword = ''
          setTimeout(() => { this.securitySuccess = null }, 3000)
        } else {
          this.securityError = response.data.message || this.t('raSettings.states.error')
        }
      } catch (error) {
        console.error('Error updating password:', error)
        if (error.response && error.response.data) {
          this.securityError = error.response.data.message || this.t('raSettings.states.error')
        } else if (error.message) {
          this.securityError = error.message
        } else {
          this.securityError = this.t('raSettings.states.error')
        }
      } finally {
        this.isUpdatingPassword = false
      }
    }
  }
}
</script>

<style scoped>
.settings {
  padding: 2rem;
  margin: 0 auto;
}

.success-message-top {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
  padding: 1rem;
  border-radius: 0.25rem;
  margin-bottom: 1rem;
  text-align: center;
}

.tabs-navigation {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 0;
}

.tab-button {
  padding: 1rem 2rem;
  border: none;
  background: none;
  font-size: 1rem;
  color: #666;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tab-button i {
  font-size: 1.1rem;
}

.tab-button:hover {
  color: #333;
}

.tab-button.active {
  color: #22c55e;
  font-weight: 500;
}

.tab-button.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #22c55e;
}
</style>